/**
 * WHICH PRODUCT THIS BUILD IS — one declaration, explicit, and FAIL-CLOSED.
 *
 * <h2>⚠ THE TARGET IS BAKED IN. ONE BUILD PER PRODUCT</h2>
 *
 * This is the consequence worth knowing before anything else, because it is a DEPLOYMENT change
 * rather than a code one. Until now a single bundle served both products and the hostname decided
 * at runtime, so one artefact could be pointed at either domain. The target is now read from
 * `productTarget.json` at build time, which means:
 *
 * <ul>
 *   <li>serving AstroStockCharts, integralDelta and dhanabhyas needs one build EACH;</li>
 *   <li>which deployment gets which is set by its own wrapper through
 *       {@link PRODUCT_TARGET_ENV}, with this file as the committed default;</li>
 *   <li>the same artefact can no longer be re-pointed by changing a DNS record;</li>
 *   <li>and in exchange, what a build is cannot be in doubt — which is the whole point. The
 *       hostname mechanism answered "probably ASC" for any domain it did not recognise, and a
 *       capability rule keyed on that silently did not apply.</li>
 * </ul>
 *
 * <h2>⚠ FAIL-CLOSED, AND AT TWO LEVELS</h2>
 *
 * A missing or invalid target STOPS THE BUILD. `scripts/validate-product-target.js` runs from
 * `prestart` and `prebuild`, so `npm start` and `npm run build` refuse before webpack is reached
 * and print what to do about it.
 *
 * <p>This module throws as well, and the second guard is not redundant. The npm hook is the one a
 * person meets; the throw covers every path that does not go through it — a test runner, a
 * Storybook, a direct `react-scripts build`, a CI step someone wires up later. Without it, a
 * bundle built by any of those would fall back to a default, which is exactly the silent
 * behaviour this replaces.
 *
 * <h2>Why a JSON file rather than an environment variable</h2>
 *
 * `REACT_APP_PRODUCT` already exists and still works — but an env var is invisible in the
 * repository: the value lives in a deploy script, a CI setting or a shell, and "what is this
 * build" cannot be answered by reading the checkout. A committed JSON file is reviewable, has a
 * diff, and makes a change to the product a change somebody approves.
 */

import target from './productTarget.json';

/** The AstroStockCharts customer product. */
export const ASTRO = 'astro';

/** The integralDelta research product. */
export const INTEGRALDELTA = 'integraldelta';

/**
 * THE DHANABHYAS PRODUCT — the India deployment, served at app.dhanabhyas.com.
 *
 * <p>⚠ ITS OWN TARGET RATHER THAN A REUSE OF {@link ASTRO}, deliberately. It denies exactly what
 * astro denies today and could simply have been astro with different branding — but the two are
 * different PRODUCTS for different markets, and reusing one target would mean the first time
 * India wanted a study astrostockcharts.com does not offer (or the reverse), the change could not
 * be expressed without touching the other site. A target is cheap; untangling two products that
 * share one is not.
 *
 * <p>So its deny-list starts as a copy of astro's and is free to diverge. See
 * {@code PRODUCT_DENIES} in toolbarProfiles.js, where that is stated at the list itself.
 */
export const DHANABHYAS = 'dhanabhyas';

/**
 * The internal build: everything visible, nothing gated by product.
 *
 * <p>⚠ NOT THE SAME THING AS THE ADMIN ROLE, and the collision in the word is worth stating.
 * `isAdmin` is a property of the signed-in USER, read from Firebase, and it still applies on
 * every target — an admin on the astro build is an admin. This is a property of the BUILD. The
 * two are independent: an ordinary customer on an admin build is still an ordinary customer, and
 * an administrator on the astro build still does not get the integralDelta-only surfaces.
 */
export const ADMIN = 'admin';

/** The four, in the order the diagnostic lists them. */
export const PRODUCT_TARGETS = Object.freeze([ASTRO, INTEGRALDELTA, DHANABHYAS, ADMIN]);

export const PRODUCT_TARGET_FILE = 'frontend/src/config/productTarget.json';

/**
 * The environment variable that OVERRIDES the committed file, for one build.
 *
 * <h2>⚠ WHY AN ENV VAR NOW, when this module's own docblock argues against one</h2>
 *
 * That argument stands and is the reason the FILE is still the default: an env var is invisible
 * in the repository, so "what is this build" could not be answered by reading the checkout.
 *
 * What changed is the requirement. Three deployments are now served from one repository —
 * astrostockcharts.com as `integraldelta`, app.dhanabhyas.com as `dhanabhyas` — and a single
 * committed value cannot be two things at once. The alternative is editing the file between
 * deploys, which is the manual, easily-forgotten step that ships the wrong product to the wrong
 * domain; this is the failure the whole mechanism exists to prevent, so trading it back for
 * reviewability would be a bad bargain.
 *
 * <p>Reviewability is kept rather than surrendered: the override lives in
 * `scripts/deploy-us.sh` and `scripts/deploy-mumbai.sh`, which are themselves committed and
 * diffed, and the build prints the resolved target. What is NOT reviewable is a value typed into
 * a shell by hand — which is exactly the case the next paragraph refuses to let pass quietly.
 *
 * <h2>⚠ AN INVALID OVERRIDE FAILS; IT DOES NOT FALL BACK TO THE FILE</h2>
 *
 * A typo'd `PRODUCT_TARGET=intergraldelta` silently building the committed default would be the
 * silent-default behaviour this module replaced, reintroduced through the new door — and worse
 * than before, because the operator would believe they had selected something. Set-but-invalid
 * is an error; unset is the file.
 *
 * <p>`REACT_APP_` prefixed because Create React App inlines only those at build time. A bare
 * `PRODUCT_TARGET` would be readable by the node guard and invisible to the bundle, so the two
 * would disagree — the guard passing a target the app then never saw.
 */
export const PRODUCT_TARGET_ENV = 'REACT_APP_PRODUCT_TARGET';

/**
 * The instructional diagnostic, shared by the build guard and the runtime throw.
 *
 * <p>⚠ IT NAMES THE FILE, THE FIELD AND THE THREE VALUES, and shows the whole file rather than
 * describing it. A reader meeting this has a build that will not start; "invalid product target"
 * would leave them searching for which of several product-ish mechanisms is complaining — there
 * are four in this codebase that resolve a product — so the message has to be the fix and not a
 * description of the problem.
 *
 * @param {unknown} found what was actually read, for the line that says what is wrong
 */
export function productTargetDiagnostic(found) {
  const shown = found === undefined || found === null || found === ''
    ? '(nothing)'
    : JSON.stringify(found);
  return [
    '',
    '  ✗ No valid product target is configured, so this build cannot continue.',
    '',
    `    read:     activeProduct = ${shown}`,
    `    expected: one of ${PRODUCT_TARGETS.map((t) => `"${t}"`).join(', ')}`,
    '',
    `  Set it in ${PRODUCT_TARGET_FILE}:`,
    '',
    '      {',
    '        "activeProduct": "astro"',
    '      }',
    '',
    '    astro          the AstroStockCharts customer product',
    '    integraldelta  the integralDelta research product',
    '    dhanabhyas     the India product, app.dhanabhyas.com',
    '    admin          the internal build; nothing is hidden by product',
    '',
    `  ...or set ${PRODUCT_TARGET_ENV} in the environment, which OVERRIDES the file. That is what`,
    '  the deploy wrappers do, so one repository can build a different product per deployment:',
    '',
    `      ${PRODUCT_TARGET_ENV}=dhanabhyas npm run build`,
    '',
    '  The target is baked into the bundle, so a deployment serving two products needs one build',
    '  per product. This is deliberate: the hostname mechanism it replaces answered "probably',
    '  astro" for any domain it did not recognise, and a rule keyed on that silently did not',
    '  apply.',
    '',
  ].join('\n');
}

/** True for one of the three. Exported so the build guard applies the same rule, not a copy. */
export const isValidProductTarget = (value) =>
  typeof value === 'string' && PRODUCT_TARGETS.includes(value.trim().toLowerCase());

/**
 * Normalises a configured value, or throws the diagnostic.
 *
 * <p>Trimmed and lower-cased, because a hand-edited JSON file carrying `"Astro"` means the astro
 * build and refusing it would be refusing the intent over a capital letter. The canonical value
 * is what every consumer sees, so nothing downstream has to repeat the comparison.
 */
export function readProductTarget(value) {
  if (!isValidProductTarget(value)) {
    throw new Error(productTargetDiagnostic(value));
  }
  return value.trim().toLowerCase();
}

/**
 * The configured value: the environment override when one is SET, else the committed file.
 *
 * <p>Returns the raw value either way, so `readProductTarget` applies one validation rule to both
 * sources and an invalid override produces the same instructional diagnostic a bad file does.
 * Checking validity here instead would be a second copy of the rule, and the copy that disagreed
 * would be the one nobody tested.
 *
 * <p>⚠ SET-BUT-EMPTY COUNTS AS UNSET. `export REACT_APP_PRODUCT_TARGET=` in a wrapper is how a
 * variable gets cleared, and CRA inlines an unset one as the string `"undefined"` in some
 * configurations — neither means "build nothing", and failing on them would make an untouched
 * wrapper unable to build at all.
 *
 * <h2>⚠ `process.env.REACT_APP_PRODUCT_TARGET` WRITTEN OUT IN FULL, and never guarded</h2>
 *
 * Two shapes that look safer and both silently break the override:
 *
 * <ul>
 *   <li><b>A `typeof process !== 'undefined'` guard.</b> Webpack's DefinePlugin replaces the
 *       expression `process.env` — it does NOT replace a bare `process`. So the guard survives
 *       into the bundle, evaluates `undefined` in a browser, and short-circuits BEFORE the
 *       inlined object is ever read: the build falls back to the committed file and a dhanabhyas
 *       deploy ships integralDelta. This was written that way first and caught only by grepping
 *       the built bundle for `typeof process`; every unit test passed, because jest runs in node
 *       where `process` is real.</li>
 *   <li><b>A dynamic key, `process.env[PRODUCT_TARGET_ENV]`.</b> It happens to work under CRA,
 *       which stringifies the WHOLE object — but that is a property of this bundler's
 *       configuration rather than of the contract, and the static member expression is the form
 *       every bundler documents.</li>
 * </ul>
 *
 * <p>So the name appears twice: once as a literal here because the build requires it, and once as
 * {@link PRODUCT_TARGET_ENV} for the diagnostic, the guard and the tests. `productTarget.test.js`
 * asserts this file contains the literal, so the two cannot drift.
 */
export function resolveConfiguredTarget(env, file) {
  // eslint-disable-next-line no-undef
  const raw = env === undefined ? process.env.REACT_APP_PRODUCT_TARGET : env;
  const override = typeof raw === 'string' ? raw.trim() : raw;
  if (override && override !== 'undefined') return override;
  const json = file === undefined ? target : file;
  return json && json.activeProduct;
}

/**
 * THE PRODUCT THIS BUILD IS. Never null — the module throws rather than default.
 *
 * <p>Evaluated at module load, so an invalid file takes the app down on the first import rather
 * than at the first feature that asks. That is the fail-closed direction: a blank screen with
 * this message in the console is recoverable in one edit, where a silently-defaulted build looks
 * correct and ships the wrong capability set.
 */
export const ACTIVE_PRODUCT = readProductTarget(resolveConfiguredTarget());

/* ── THE ADMIN INSPECTION OVERRIDE ──────────────────────────────────────────────────────────── */

/**
 * Where an administrator's chosen target is kept. Part of the contract — it is in localStorage.
 */
export const PRODUCT_OVERRIDE_STORAGE_KEY = 'product_target_override';

/**
 * THE TARGET AN ADMINISTRATOR IS CURRENTLY INSPECTING, or the baked one.
 *
 * <h2>⚠ `isAdmin` IS A PARAMETER, AND IT IS THE WHOLE SECURITY ARGUMENT</h2>
 *
 * localStorage is writable by whoever owns the browser. Without the role test, a customer on
 * integralDelta.ai could type one line into a console — `localStorage.setItem(
 * 'product_target_override', 'astro')` — and reveal every surface the deny list exists to remove.
 * That is the shape this codebase has already paid for twice: "a hidden button is not a guard",
 * and a switch whose state the UI showed, could not change, and sent anyway.
 *
 * <p>So the override is not merely HIDDEN from a non-admin, it is UNREADABLE by one: anything
 * other than a true `isAdmin` resolves to {@link ACTIVE_PRODUCT}, the value the build committed.
 * Callers hold the raw stored value in state and derive the effective one through this, which also
 * means a demoted administrator loses the override on the render their role changes — no effect to
 * fire, nothing to invalidate.
 *
 * <p>⚠ AND IT CHANGES WHAT IS RENDERED, NOTHING ELSE. Every endpoint keeps its own
 * authorisation, exactly as the toolbar profile's note says: an administrator who flips to `astro`
 * on an integralDelta build sees the astro surfaces and is still refused by the backend for
 * anything they are not entitled to. It is an INSPECTION tool — "what does the other product look
 * like from here" — and never a grant.
 *
 * @param isAdmin the live role. Anything falsy returns the build's own target.
 */
export const readProductOverride = (isAdmin) => {
  if (!isAdmin) return ACTIVE_PRODUCT;
  try {
    const raw = window.localStorage.getItem(PRODUCT_OVERRIDE_STORAGE_KEY);
    return isValidProductTarget(raw) ? raw.trim().toLowerCase() : ACTIVE_PRODUCT;
  } catch (e) {
    // Storage can be unavailable (private mode) or hold junk. Either way the build's own target
    // is the right answer, and it is the SAFE one — never the most permissive of the three.
    return ACTIVE_PRODUCT;
  }
};

/**
 * Persists an administrator's choice, so a reload does not silently return them to the baked
 * target in the middle of comparing two products.
 *
 * <p>An invalid value CLEARS rather than storing — the reader would ignore it anyway, and a
 * key holding junk reads as a corrupted setting rather than as an absent one.
 */
export const writeProductOverride = (value) => {
  try {
    if (!isValidProductTarget(value)) {
      window.localStorage.removeItem(PRODUCT_OVERRIDE_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(PRODUCT_OVERRIDE_STORAGE_KEY, value.trim().toLowerCase());
  } catch (e) {
    // A switch that works for the session is better than one that throws on a stored write.
  }
};

/** Back to whatever the build committed. Separate from writing, so "reset" reads as itself. */
export const clearProductOverride = () => writeProductOverride(null);
